import './styles.scss';
import React from 'react';
import FlexicoDynamicTableRefreshComponent from "../FlexicoDynamicTableRefreshComponent";
import CustomMultiValuedParameterRow from "../../Templates/components/CustomMultiValuedParameterRow";
import { FLEXICO_CUSTOM_ATTRIBUTE_TYPES, FLEXICO_TEMPLATE_KEYS } from "../Constants";
import { reorderFieldsOnlyByIndex, sortParametersByGroupOrder } from "../Utility";
import { CodeView } from '@amzn/awsui-code-view';
import {
    Button,
    Box,
    SpaceBetween,
    Table,
    Modal,
} from "@amzn/awsui-components-react";
import hiddenIcon from '../../../images/hide-svgrepo-com.svg';
import DraggableGroupedList, { Buckets, Row } from './DraggableGroupedList';

type MandatoryParameter = {
  label: string;
  field_type: string;
  limit?: number;
};

type ParameterPermission = {
  type: string;
  alias: string;
}

export type Parameter = {
  field_id?: string;
  field_type?: string;
  field_values?: string[];
  label?: string;
  display_name?: string | null;
  info?: any;
  source_database?: string | null;
  is_mandatory?: boolean;
  is_hidden?: boolean | null;
  display_index?: number | string | null | undefined;
  group_id?: string;
  group_name?: string;
  group_order?: number;
  field_tokens?: Array<string | { label: string; dismissLabel: string }>;
  limit?: number;
  limit_permissions?: boolean;
  permissions?: Array<ParameterPermission>;
  [key: string]: any; // Allow additional properties
};

type Rows = Record<string, Row<Parameter>>;

interface ParameterDetailsProps {
  isEditMode: boolean
  isNewFlexicoTemplate: boolean
  canEdit: boolean
  isDynamicInputParametersAvailable: boolean
  showRefreshModal: React.Dispatch<React.SetStateAction<boolean>>
  refreshModal: boolean
  fid: string
  id: string
  flexicoTemplateDataEdited: Parameter
  getValue: (fieldName: string, subKey?: string | null) => any
  postProcessTeamEmails: (value: string) => string
  mandatoryParameters: MandatoryParameter[]
  updateValue: (fieldName: string, value: any, subKey?: string | null) => void
}

const ParameterDetails: React.FC<ParameterDetailsProps> = ({
  isEditMode,
  isNewFlexicoTemplate,
  canEdit,
  isDynamicInputParametersAvailable,
  showRefreshModal,
  refreshModal,
  fid,
  id,
  flexicoTemplateDataEdited,
  getValue,
  postProcessTeamEmails,
  mandatoryParameters,
  updateValue
}) => {
  // Transform parameter data before converting to rows
  const transformParameter = (param: Parameter, index: number): Parameter => {
    let updated = { ...param };

    // set display_index for grouping functionality
    if (updated.display_index === null || updated.display_index === undefined) {
      updated.display_index = index;
    } else {
      updated.display_index = parseInt(String(updated.display_index));
    }

    // Set group properties if missing
    if (!updated.hasOwnProperty("group_id") || updated.group_id === null || updated.group_id === undefined) {
      updated["group_id"] = 'ungrouped';
      updated["group_name"] = 'Ungrouped';
      updated["group_order"] = 0;
    }

    // Convert field_values to field_tokens if needed
    if (updated.hasOwnProperty('field_values') && !updated.hasOwnProperty('field_tokens')) {
      let tokens = [];
      for (let j = 0; j < (updated['field_values']?.length || 0); j++) {
        let val = updated['field_values']![j];
        if (updated.field_type != 'dynamic_select' && updated.field_type != 'dynamic_multi_select') {
          tokens.push({ label: val, dismissLabel: val });
        } else {
          tokens.push(val);
        }
      }
      updated['field_tokens'] = tokens;
    }

    // Add limit from mandatoryParameters if missing
    if (!updated.hasOwnProperty('limit') && mandatoryParameters) {
      for (let i = 0; i < mandatoryParameters?.length; i++) {
        const mandatoryParam = mandatoryParameters[i];
        if (mandatoryParam.label == updated.label && mandatoryParam.field_type == updated.field_type) {
          if (mandatoryParam.limit) {
            updated['limit'] = mandatoryParam.limit;
          }
          break;
        }
      }
    }

    // Hardcoded fallback: Add limit for contico_job if still missing
    if (updated.label == 'contico_job' && updated.field_type == 'fixed_inputs' && !updated.hasOwnProperty('limit')) {
      updated['limit'] = 1;
    }

    return updated;
  };

  // Convert parameters array to DraggableGroupedList format
  const convertParametersToRows = (parameters: Parameter[]) => {
    if (!parameters || parameters.length === 0) return {};
    const rows: Rows = {};
    parameters.forEach((param, index) => {
      const id = param.id || `param-${index}`;
      const transformed = transformParameter(param, index);
      rows[id] = { ...transformed, id };
    });
    return rows;
  };

  const convertParametersToInitialData = () => {
    const parameters = getValue(FLEXICO_TEMPLATE_KEYS.parameters) || [];

    // Ensure mandatory parameters are always present (e.g. contico_job / fixed_inputs)
    if (mandatoryParameters) {
      for (const mp of mandatoryParameters) {
        const found = parameters.some((p: Parameter) => p.label === mp.label);
        if (!found) {
          parameters.push({
            field_type: mp.field_type,
            label: mp.label,
            display_name: mp.label,
            limit: mp.limit,
            field_values: [],
            field_tokens: [],
          });
        }
      }
    }
    const attributes = getValue(FLEXICO_TEMPLATE_KEYS.custom_attributes) || [];
    
    // Parse custom_attributes to extract grouping info
    let groupInfo: Record<string, { group_name: string; group_order: string }> = {};
    let parameterMappingByLabel: Record<string, string> = {};
    
    const groupingAttr = Array.isArray(attributes) 
      ? attributes.find((attr: any) => attr.attribute_type === FLEXICO_CUSTOM_ATTRIBUTE_TYPES.GROUPING_PREFERENCE && attr.sub_type === 'FLEXICO')
      : attributes;
    
    if (groupingAttr?.attribute) {
      try {
        const parsed = JSON.parse(groupingAttr.attribute);
        groupInfo = parsed.group_info || {};
        parameterMappingByLabel = parsed.parameter_mapping_by_label || {};
        // Backward compat: migrate old field_id-based parameter_mapping to label-based
        if (Object.keys(parameterMappingByLabel).length === 0 && parsed.parameter_mapping) {
          const oldMapping = parsed.parameter_mapping;
          parameters.forEach((param: Parameter) => {
            const fieldId = param.field_id;
            if (fieldId && oldMapping[fieldId] && param.label) {
              parameterMappingByLabel[param.label] = oldMapping[fieldId];
            }
          });
        }
      } catch (e) {
        console.error('Failed to parse custom_attributes:', e);
      }
    }
    
    // Apply group properties to parameters from custom_attributes
    const enrichedParams = parameters.map((param: Parameter, index: number) => {
      const label = param.label ;
      const groupId = label ? parameterMappingByLabel[label] : null;
      
      if (groupId && groupInfo[groupId]) {
        return {
          ...param,
          group_id: groupId,
          group_name: groupInfo[groupId].group_name,
          group_order: parseInt(groupInfo[groupId].group_order)
        };
      }
      return param;
    });
    
    const rows = convertParametersToRows(enrichedParams);

    const bucketMap: Buckets = {};
    // Add all groups from groupInfo, including empty ones
    Object.entries(groupInfo).forEach(([groupId, info]) => {
      bucketMap[groupId] = {
        id: groupId,
        title: info.group_name || 'Unnamed Group',
        rowIds: Object.values(rows).filter((r: Row<Parameter>) => r.group_id === groupId).map((r: Row<Parameter>) => r.id)
      };
    });
    
    const sortedBucketOrder = Object.keys(bucketMap).sort((a, b) => {
      const orderA = parseInt(groupInfo[a]?.group_order) || 0;
      const orderB = parseInt(groupInfo[b]?.group_order) || 0;
      return orderA - orderB;
    });
    
    return {
      rows,
      buckets: bucketMap,
      bucketOrder: sortedBucketOrder,
    };
  };

  const canDeleteParameter = (parameter: Parameter) => {
    for (let i = 0; i < mandatoryParameters?.length; i++) {
      const mandatoryParam = mandatoryParameters[i];
      if (mandatoryParam.label === parameter.label && mandatoryParam.field_type === parameter.field_type) {
        return false;
      }
    }
    return true;
  };

  // Helper to get group name for a parameter from custom_attributes
  const getParameterGroupName = (param: Parameter): string => {
    const attributes = getValue(FLEXICO_TEMPLATE_KEYS.custom_attributes) || [];
    const groupingAttr = Array.isArray(attributes) 
      ? attributes.find((attr: any) => attr.attribute_type === FLEXICO_CUSTOM_ATTRIBUTE_TYPES.GROUPING_PREFERENCE && attr.sub_type === 'FLEXICO')
      : null;
    
    if (groupingAttr?.attribute) {
      try {
        const parsed = JSON.parse(groupingAttr.attribute);
        const label = param.label;
        // Try new label-based mapping first, then fall back to old field_id-based mapping
        const groupId = (label && parsed.parameter_mapping_by_label?.[label])
          || (param.field_id && parsed.parameter_mapping?.[param.field_id]);
        if (groupId) {
          return parsed.group_info?.[groupId]?.group_name || "Ungrouped";
        }
      } catch (e) {
        return "Ungrouped";
      }
    }
    return "Ungrouped";
  };

  return (
    <div className="parameter-details--container">
      <SpaceBetween size="l">
        <SpaceBetween direction="horizontal" size="l">
          <Box variant="h1">Parameter(s) Details{isEditMode && <Box variant="span" color="text-status-error"> *</Box>}</Box>
          {
            !isNewFlexicoTemplate && canEdit && isDynamicInputParametersAvailable && <Button variant="inline-link" onClick={()=>{showRefreshModal(true)}}>
              Refresh dynamic inputs
            </Button>
          }
          <Modal
            onDismiss={() => showRefreshModal(false)}
            visible={refreshModal}
            header={<Box variant="h2">Refresh dynamic inputs</Box>}
            size="max"
          >
            <Box>
              <FlexicoDynamicTableRefreshComponent flex_id={fid} app_id={id} refresh={refreshModal}></FlexicoDynamicTableRefreshComponent>
            </Box>
          </Modal>
        </SpaceBetween>
        {  
          isEditMode ? <>
            <DraggableGroupedList
              initialRows={convertParametersToInitialData().rows}
              initialBuckets={convertParametersToInitialData().buckets}
              initialBucketOrder={convertParametersToInitialData().bucketOrder}
              onDataChange={(updatedRows, updatedBuckets, updatedBucketOrder) => {
                // Build group_info and parameter_mapping_by_label from updatedRows and updatedBuckets
                const groupInfo: Record<string, { group_name: string; group_order: string }> = {};
                const parameterMappingByLabel: Record<string, string> = {};
                
                updatedBucketOrder.forEach((bucketId, index) => {
                  const bucket = updatedBuckets[bucketId];
                  groupInfo[bucketId] = {
                    group_name: bucket.title,
                    group_order: String(index + 1)
                  };
                  bucket.rowIds.forEach(rowId => {
                    const row = updatedRows[rowId] as any;
                    const label = row.label;
                    parameterMappingByLabel[label] = bucketId;
                  });
                });
                
                const paramsArray = Object.values(updatedRows).map(row => {
                  const { id, group_id, group_name, group_order, ...param } = row;
                  
                  // Sync field_values from field_tokens
                  if (param.field_type === 'input' || param.field_type === 'date_picker' || param.field_type === 'date_time_picker') {
                    param.field_values = [' '];
                  } else if (param.field_tokens && param.field_tokens.length > 0) {
                    const values: string[] = [];
                    for (let token of param.field_tokens) {
                      if (param.field_type === 'dynamic_select' || param.field_type === 'dynamic_multi_select') {
                        values.push(typeof token === 'string' ? token : String(token));
                      } else {
                        values.push(typeof token === 'string' ? token : token.label);
                      }
                    }
                    param.field_values = values;
                  }
                  
                  // Convert display_index to string for backend
                  if (param.display_index !== undefined && param.display_index !== null) {
                    param.display_index = String(param.display_index);
                  }
                  
                  return param;
                });
                
                // Get existing custom_attributes array or create new one
                const existingAttributes = getValue(FLEXICO_TEMPLATE_KEYS.custom_attributes) || [];
                // Remove all existing GROUPING_PREFERENCE entries (including any broken/duplicate ones)
                const attributesArray = (Array.isArray(existingAttributes) ? existingAttributes : [])
                  .filter((attr: any) => !(
                    (attr.attribute_type === FLEXICO_CUSTOM_ATTRIBUTE_TYPES.GROUPING_PREFERENCE && attr.sub_type === 'FLEXICO') ||
                    (!attr.attribute_type && attr.sub_type === 'FLEXICO' && attr.attribute && attr.attribute.includes('parameter_mapping_by_label'))
                  ));
                
                const groupingAttr = {
                  attribute_type: FLEXICO_CUSTOM_ATTRIBUTE_TYPES.GROUPING_PREFERENCE,
                  sub_type: "FLEXICO",
                  attribute: JSON.stringify({
                    group_info: groupInfo,
                    parameter_mapping_by_label: parameterMappingByLabel
                  })
                };
                
                attributesArray.push(groupingAttr);
                
                updateValue(FLEXICO_TEMPLATE_KEYS.custom_attributes, attributesArray);
                updateValue(FLEXICO_TEMPLATE_KEYS.parameters, paramsArray);
              }}
              onAddRow={() => {
                const currentParams = getValue(FLEXICO_TEMPLATE_KEYS.parameters) || [];
                const ungroupedParams = currentParams.filter((p: Parameter) => !p.group_id || p.group_id === 'ungrouped');
                const maxDisplayIndex = ungroupedParams.length > 0 
                  ? Math.max(...ungroupedParams.map((p: Parameter) => parseInt(String(p.display_index)) || 0))
                  : -1;
                
                const newRow = {
                  label: '',
                  field_tokens: [],
                  field_type: [],
                  field_values: [],
                  display_index: maxDisplayIndex + 1,
                  limit_permissions: false,
                  permissions: []
                };
                const updatedParams = [...currentParams, newRow];
                updateValue(FLEXICO_TEMPLATE_KEYS.parameters, updatedParams);
              }}
              onDeleteRow={(rowId) => {
                const currentParams = getValue(FLEXICO_TEMPLATE_KEYS.parameters) || [];
                const updatedParams = currentParams.filter((param: any) => {
                  const paramId = param.id || `param-${currentParams.indexOf(param)}`;
                  return paramId !== rowId;
                });
                updateValue(FLEXICO_TEMPLATE_KEYS.parameters, updatedParams);
              }}
              renderItem={(row, { onEdit }) => ({
                content: <CustomMultiValuedParameterRow
                  parameter={row}
                  onEdit={(updatedParam: any) => {
                    onEdit(updatedParam);
                  }}
                  canDelete={canDeleteParameter(row)}
                  hideLabelPrefix="flexico_internal"
                  postProcess={postProcessTeamEmails}
                  isCriticalData={flexicoTemplateDataEdited?.critical_data}
                />,
                canDelete: canDeleteParameter(row)
              })}
            />
            </>
            :
            <div>
              {getValue(FLEXICO_TEMPLATE_KEYS.parameters) ? <Table<Parameter>
                columnDefinitions={[
                  {
                    id: "type",
                    header: "Type",
                    cell: item => item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.field_type] || "-",
                    sortingField: "alt"
                  },
                  {
                    id: "parameter",
                    header: "Parameter",
                    cell: item => 
                      (
                        <Box>
                          {item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.label]}
                          {
                            item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.is_mandatory]
                            ? <Box variant="span" color="text-status-error"> *</Box>
                            :''}
                          {
                            item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.is_hidden]
                            ? <Box float="right">
                              <img className="small_icon" src={hiddenIcon}></img>
                            </Box> 
                            : ''
                          } 
                        </Box>
                      ),
                    isRowHeader: true
                  },
                  {
                    id: "displayName",
                    header: "Display name",
                    cell: item => item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.display_name] || item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.label] || "-",
                    isRowHeader: true
                  },
                  {
                    id: "groupName",
                    header: "Group",
                    cell: item => getParameterGroupName(item),
                    isRowHeader: true
                  },
                  {
                    id: "displayIndex",
                    header: "Display index",
                    cell: item => item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.display_index] || "-",
                    isRowHeader: true
                  },
                  {
                    id: "values",
                    header: "Values",
                    cell: item => {
                      if(item.field_type == 'dynamic_select' || item.field_type == 'dynamic_multi_select'){
                        return (
                          <SpaceBetween direction="vertical" size="s">
                            <CodeView 
                              content={item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.field_values]?.join() || item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.field_values] || ''}
                              lineNumbers
                            />
                            {
                              item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.source_database]? <Box>Source: {item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.source_database]}</Box>:<></>
                            }
                          </SpaceBetween>
                        )
                      }else{
                        return item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.field_values]?.join() || item[FLEXICO_TEMPLATE_KEYS.PARAMETER_KEYS.field_values]
                      }
                    },
                    isRowHeader: true
                  },
                ]}
                items={sortParametersByGroupOrder(getValue(FLEXICO_TEMPLATE_KEYS.parameters), getValue(FLEXICO_TEMPLATE_KEYS.custom_attributes))}
                loadingText="Loading parameters"
                sortingDisabled
                empty={
                  <Box
                    margin={{ vertical: "xs" }}
                    textAlign="center"
                    color="inherit"
                  >
                    <SpaceBetween size="m">
                      <b>No parameters</b>
                    </SpaceBetween>
                  </Box>
                }
                variant='embedded'
              /> : ''}
            </div>
        }
      </SpaceBetween>
    </div>
  )
}

export default ParameterDetails;